/*
  CAI Cities Storymap — Light mode, single-page scrollable
  Fonts: Barlow Condensed (headings) + Source Sans 3 (body) via Google Fonts
  Add to index.css:
    @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700&family=Source+Sans+3:ital,wght@0,400;0,600;1,400&display=swap');
*/

import HeroMap from "../components/storymap/HeroMap";
import caiLogo from "../assets/CAI White.png";

const CITIES = [
    {
        key: "seattle",
        name: "SEATTLE",
        state: "WA",
        theme: "Connectivity & Opportunity",
        color: "#7B5FD4",
        colorDark: "#3C3489",
        colorBg: "#ebe9f0",
        colorBorder: "#7B5FD4",
        accentText: "#3C3489",
        mapX: 84, mapY: 62,
        tagline:
            "A globally connected and educated city where strong household incomes coexist with housing cost pressures and economic variation.",
        stats: [
            { v: "~758K", l: "Population" },
            { v: "20%", l: "Foreign Born" },
            { v: "Very High", l: "Internet Access" },
            { v: "Drive Alone + Transit", l: "Top Commute Mode" },
        ],
        bars: [
            { label: "Housing cost burden", value: 0.42, note: "42% of renters" },
            { label: "Postsecondary attainment", value: 0.68, note: "High" },
            { label: "Household Income", value: 0.8, note: "$130k" },
        ],
        paras: [
            "Seattle is home to approximately 758,000 residents, with population characteristics shaped by international migration, a predominantly White population alongside a sizable Asian population, and high educational attainment. One-fifth of residents are foreign born, while most households primarily use English; disabilities also affect a segment of the population, reinforcing the need for accessible infrastructure and services.",
            "High rates of high school and postsecondary completion support workforce participation and are closely tied to Seattle's income profile. Widespread access to internet service, computers, and health insurance enables residents to engage in employment, education, and essential services, contributing to everyday connectivity and economic participation.",
            "Despite strong household incomes overall, economic conditions vary, with some residents living below or near poverty thresholds. Housing costs are a central factor: many households — particularly renters — face significant cost burdens, and residential turnover reflects ongoing movement within the city. Travel behavior is shaped by regional employment patterns, with driving alone the most common commute mode, followed by transit.",
        ],
        flip: false,
        altBg: false,
    },
    {
        key: "denver",
        name: "DENVER",
        state: "CO",
        theme: "Access & Affordability",
        color: "#B8C826",
        colorDark: "#6a7010",
        colorBg: "#f0f0e0",
        colorBorder: "#B8C826",
        accentText: "#6a7010",
        mapX: 198, mapY: 112,
        tagline:
            "Broad educational attainment and access to services, shaped by housing costs, income variation, and mobility patterns.",
        stats: [
            { v: "~718K", l: "Population" },
            { v: "91%", l: "HS+ Attain." },
            { v: "Wide", l: "Health Ins." },
            { v: "Drive Alone", l: "Top Commute Mode" },
        ],
        bars: [
            { label: "Housing cost burden", value: 0.65, note: "46% of renters" },
            { label: "Resident Turnover", value: 0.20, note: "20%" },
            { label: "Computer and Internet access", value: 0.94, note: "Very High" },
        ],
        paras: [
            "Denver is home to approximately 718,000 residents, with population characteristics that reflect steady growth, a predominantly White racial composition, and broad educational attainment. A modest share of residents are foreign born, contributing to workforce participation and community composition without defining Denver's overall population profile.",
            "Widespread access to internet service, computers, and health insurance enables residents to engage in employment, education, and essential services — reinforcing everyday connectivity and workforce stability. High rates of high school and postsecondary completion support participation across a range of industries and occupations.",
            "Despite relatively strong household incomes overall, economic conditions vary across households. Housing costs are a key driver: many households — particularly renters — face significant cost burdens, and residential turnover indicates frequent movement within the city. Most residents commute by driving alone, with carpooling as a secondary mode.",
        ],
        flip: true,
        altBg: true,
    },
    {
        key: "memphis",
        name: "MEMPHIS",
        state: "TN",
        theme: "Resilience & Stability",
        color: "#3EC8E8",
        colorDark: "#0e6a80",
        colorBg: "#e0f6fa",
        colorBorder: "#3EC8E8",
        accentText: "#0e6a80",
        mapX: 336, mapY: 145,
        tagline:
            "A city where intersecting challenges of resource access, housing affordability, and economic mobility shape resident stability.",
        stats: [
            { v: "~640K", l: "Population" },
            { v: "Majority", l: "Cost Burdened" },
            { v: "Higher", l: "Unemployment" },
            { v: "Drive Alone", l: "Top Commute Mode" },
        ],
        bars: [
            { label: "Computer and Internet access", value: 0.89, note: "High" },
            { label: "Housing cost burden", value: 0.84, note: "Majority renters" },
            { label: "Poverty rate", value: 0.70, note: "High" },
        ],
        paras: [
            "Memphis is home to approximately 660,000 residents, with population characteristics shaped by a predominantly Black or African American population, lower levels of international migration, and varied educational attainment. A relatively small share of residents are foreign born; disabilities affect a notable portion of the population, reinforcing the importance of accessible housing, transportation, and public services.",
            "Access to core resources such as internet service, computers, and health insurance is present for most households, though coverage is less consistent than in some peer cities. Economic conditions reflect comparatively lower household incomes alongside significant financial strain, with substantial shares of residents living below or near poverty thresholds and higher unemployment impacting household stability.",
            "Housing costs play a central role: a majority of renter households and a quarter of owners are cost burdened, while elevated vacancy rates point to ongoing affordability challenges and uneven neighborhood stability. Together, these conditions illustrate how population composition, resource access, education, and housing intersect to shape opportunity for residents across Memphis.",
        ],
        flip: false,
        altBg: false,
    },
];

function MiniBarChart({ bars, color, colorBg, accentText }: any) {
    return (
        <svg
            viewBox="0 0 280 160"
            style={{
                width: "100%",
                borderRadius: "8px",
                background: "#fff",
                border: "1px solid #e0ddd6",
                display: "block",
            }}
            aria-label="Illustrative city indicators"
        >
            {bars.map((bar: any, i: any) => {
                const y = 18 + i * 46;
                const barW = Math.round(bar.value * 180);
                return (
                    <g key={i}>
                        <text x="14" y={y} fontSize="8" fill="#999" fontFamily="Barlow Condensed, sans-serif" letterSpacing="0.1em">
                            {bar.label.toUpperCase()}
                        </text>
                        <rect x="14" y={y + 8} width="180" height="14" rx="3" fill={colorBg} />
                        <rect x="14" y={y + 8} width={barW} height="14" rx="3" fill={color} opacity="0.75" />
                        <text x={14 + barW + 4} y={y + 19} fontSize="9" fill={accentText} fontFamily="Barlow Condensed, sans-serif" fontWeight="700">
                            {bar.note}
                        </text>
                    </g>
                );
            })}
            <text x="14" y="150" fontSize="8" fill="#ccc" fontFamily="Source Sans 3, sans-serif" fontStyle="italic">
                Illustrative relative measures
            </text>
        </svg>
    );
}

function CitySection({ city }: any) {
    const dataPanel = (
        <div style={{ width: "100%", maxWidth: "300px" }}>
            <div style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontSize: "10px",
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: city.accentText,
                marginBottom: "6px",
            }}>
                {city.theme}
            </div>
            <div style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontWeight: "700",
                fontSize: "42px",
                color: "#1a2a3a",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                lineHeight: 1,
            }}>
                {city.name}
                <span style={{ fontSize: "20px", fontWeight: "400", color: "#888", marginLeft: "6px" }}>
                    {city.state}
                </span>
            </div>
            <div style={{
                width: "36px",
                height: "3px",
                background: city.color,
                margin: "10px 0 16px",
            }} />
            <p style={{
                fontSize: "12.5px",
                color: "#444",
                lineHeight: "1.65",
                fontStyle: "italic",
                borderLeft: `2px solid ${city.color}`,
                paddingLeft: "12px",
                margin: "0 0 20px",
                fontFamily: "'Source Sans 3', sans-serif",
            }}>
                {city.tagline}
            </p>
            {/* Stats */}
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "20px" }}>
                {city.stats.map((s: any) => (
                    <div key={s.l} style={{
                        flex: "1",
                        minWidth: "80px",
                        background: "#fff",
                        border: "1px solid #e0ddd6",
                        borderRadius: "8px",
                        padding: "10px 12px",
                        textAlign: "center",
                    }}>
                        <div style={{
                            fontFamily: "'Barlow Condensed', sans-serif",
                            fontSize: "16px",
                            fontWeight: "700",
                            color: city.accentText,
                            lineHeight: 1,
                        }}>
                            {s.v}
                        </div>
                        <div style={{
                            fontFamily: "'Barlow Condensed', sans-serif",
                            fontSize: "9px",
                            color: "#888",
                            letterSpacing: "0.1em",
                            textTransform: "uppercase",
                            marginTop: "4px",
                        }}>
                            {s.l}
                        </div>
                    </div>
                ))}
            </div>
            <MiniBarChart bars={city.bars} color={city.color} colorBg={city.colorBg} accentText={city.accentText} />
        </div>
    );

    const copyPanel = (
        <div style={{
            padding: "40px 36px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
        }}>
            {city.paras.map((p: any, i: any) => (
                <p key={i} style={{
                    fontSize: "13.5px",
                    color: "#333",
                    lineHeight: "1.75",
                    margin: i < city.paras.length - 1 ? "0 0 14px" : "0",
                    fontFamily: "'Source Sans 3', sans-serif",
                }}>
                    {p}
                </p>
            ))}
        </div>
    );

    const borderSide = city.flip
        ? { borderLeft: `3px solid ${city.color}` }
        : { borderRight: `3px solid ${city.color}` };

    return (
        <div
            className="city-section"
            style={{
                background: city.altBg ? "#fafaf7" : "#f5f4f0",
            }}
        >
            {city.flip ? (
                <>
                    <div className="city-copy-panel">{copyPanel}</div>
                    <div className="city-data-panel" style={{ ...borderSide }}>
                        {dataPanel}
                    </div>
                </>
            ) : (
                <>
                    <div className="city-data-panel" style={{ ...borderSide }}>
                        {dataPanel}
                    </div>
                    <div className="city-copy-panel">{copyPanel}</div>
                </>
            )}
        </div>
    );
}

export default function CitiesStorymap() {
    return (
        <>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700&family=Source+Sans+3:ital,wght@0,400;0,600;1,400&display=swap');
        .cai-storymap * { box-sizing: border-box; }
      `}</style>
            <div className="cai-storymap" style={{ fontFamily: "'Source Sans 3', sans-serif", background: "#f5f4f0" }}>

                {/* Hero */}
                <div className="hero-section">
                    <div>
                        <a
                            href="https://communityattributes.com/"
                            target="_blank"
                            style={{
                                fontFamily: "'Barlow Condensed', sans-serif",
                                fontSize: "11px",
                                letterSpacing: "0.22em",
                                textTransform: "uppercase",
                                color: "rgba(255,255,255,0.4)",
                                marginBottom: "10px",
                            }}>
                            Community Attributes Inc.
                        </a>
                        <h1 style={{
                            fontFamily: "'Barlow Condensed', sans-serif",
                            fontWeight: "700",
                            fontSize: "44px",
                            color: "#fff",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            lineHeight: "1.05",
                            margin: "0 0 14px",
                        }}>
                            Three Cities,<br />One Story
                        </h1>
                        <p style={{
                            fontSize: "13px",
                            color: "rgba(255,255,255,0.55)",
                            lineHeight: "1.7",
                            margin: "0 0 24px",
                            maxWidth: "320px",
                        }}>
                            Explore how population, housing, access, and economic conditions shape stability and opportunity across CAI's three office cities.
                        </p>
                        <div style={{ display: "flex", gap: "24px" }}>
                            {[
                                { color: "#7B5FD4", label: "SEATTLE" },
                                { color: "#B8C826", label: "DENVER" },
                                { color: "#3EC8E8", label: "MEMPHIS" },
                            ].map((c) => (
                                <div key={c.label} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <div style={{ width: "10px", height: "10px", borderRadius: "50%", background: c.color }} />
                                    <span style={{
                                        fontFamily: "'Barlow Condensed', sans-serif",
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        color: "rgba(255,255,255,0.7)",
                                        letterSpacing: "0.08em",
                                    }}>
                                        {c.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <HeroMap />
                </div>

                {/* City sections */}
                {CITIES.map((city) => (
                    <div key={city.key}>
                        <div style={{ height: "1px", background: "#dddbd4" }} />
                        <CitySection city={city} />
                    </div>
                ))}

                {/* Footer */}
                <div style={{ height: "1px", background: "#dddbd4" }} />
                <div style={{
                    background: "#1a2a3a",
                    padding: "24px 40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "12px",
                }}>
                    <a href="https://communityattributes.com/" target="_blank" style={{
                        fontFamily: "'Barlow Condensed', sans-serif",
                        fontWeight: "700",
                        fontSize: "15px",
                        color: "#fff",
                        letterSpacing: "0.12em",
                    }}>
                        COMMUNITY ATTRIBUTES INC.
                    </a>
                    <div style={{ display: "flex", gap: "20px" }}>
                        {["SEATTLE", "DENVER", "MEMPHIS"].map((c) => (
                            <span key={c} style={{
                                fontFamily: "'Barlow Condensed', sans-serif",
                                fontSize: "11px",
                                color: "rgba(255,255,255,0.4)",
                                letterSpacing: "0.1em",
                            }}>
                                {c}
                            </span>
                        ))}
                    </div>
                    <a href="https://communityattributes.com/" target="_blank">
                        <img src={caiLogo} alt="CAI logo" width={100} height={100} />
                    </a>
                </div>

            </div>
        </>
    );
}